/**
 * agentChatAdapter — 화면이 챗에 주입하는 **정책 채널**.
 *
 * Agent Chat 은 모든 화면 위에 얹히는 범용 기능이다. 그런데 시나리오 플래닝을 지원하며 자란
 * 탓에 "4단계가 있다", "구성 단계는 1번이다", "scn.confirm 은 되돌릴 수 없다", "슬래시 팔레트
 * 카테고리는 tpl.add.* 로 나뉜다" 같은 **한 화면의 정책**이 패널 안에 하드코딩돼 있었다.
 * 그런 지식을 여기로 모아 화면이 주입하게 한다. 미제공 화면은 기본값(= 아무 정책 없음)으로 동작한다.
 *
 * ⚠️ 어댑터 객체는 화면에서 **identity 를 영구 고정**해야 한다(`useMemo(..., [])` + ref 미러).
 *   매 렌더 새 객체를 publish 하면 publish → 재렌더 → publish 루프가 난다.
 *
 * 무엇을 여기 넣고 무엇을 넣지 않는가:
 *   · 넣는다 — 단계 구조, 시나리오 상한, 화면 레지스트리 id 규칙(슬래시 분류·되돌리기 불가 판정),
 *              백엔드로 보낼 화면 고유 payload 필드
 *   · 넣지 않는다 — 백엔드 프로토콜 어휘(set_param·run_engine 등 action verb)와 도구 응답
 *              렌더러. 그건 챗과 에이전트 사이의 계약이지 화면 지식이 아니다(agentChatProtocol.js).
 */

export const DEFAULT_ADAPTER = {
  /**
   * 백엔드 chat payload 에 실을 **화면 고유 필드**를 만든다.
   * 챗은 봉투(tab·current_step·step_label·action_hint·available_actions·is_follow_up)만 소유하고,
   * plan_ver_id·scenarios·current_params 같은 도메인 필드는 전부 여기서 온다.
   * @type {null | (() => object)}
   */
  buildContext: null,

  /**
   * 단계(step) 구조. 단계 개념이 없는 화면은 null — 그러면 "다음 단계" 발화가 일반 챗으로 폴백한다.
   *   nameKeys   : 단계 이름 i18n 키 배열(인덱스 = step 번호)
   *   composeStep: 시나리오를 구성하는 단계 번호. 구성 선택지가 도착했는데 다른 단계에 있으면
   *                먼저 이 단계로 이동한다. 해당 개념이 없으면 null.
   * @type {null | { nameKeys: string[], composeStep: number|null }}
   */
  steps: null,

  /** 시나리오 구성 상한. 미제공 화면은 무제한. */
  maxScenarios: Infinity,

  /**
   * 이 액션이 "시나리오를 구성하는" 동작인가 — 선택지 패널을 닫지 않고 이어서 고르게 할지 판단한다.
   * @type {(actionVerb: string, dispatchId: string) => boolean}
   */
  isComposingAction: () => false,

  /**
   * 이 레지스트리 id 가 되돌리기 어려운 동작인가(확정·삭제 등).
   * true 면 챗이 **자동 실행하지 않고** 반드시 사용자 클릭을 받는다.
   *
   * ⚠️ 안전 장치다. 여기서 false 를 잘못 내면 사용자가 클릭한 적 없는데 확정·릴리즈가 실행된다.
   *   그래서 기본값은 "안전 측" — 어댑터를 주지 않은 화면에서도 confirm/remove/delete 어휘가
   *   든 id 는 클릭을 받는다. 화면이 이 함수를 제공하면 그 판정이 기본값을 **대체**하므로,
   *   자기 화면의 위험 id 를 빠짐없이 포함시켜야 한다.
   *   (백엔드 verb 기준 가드 confirm·remove_scenario 는 챗이 별도로 항상 적용한다 — 이중 안전망.)
   * @type {(dispatchId: string) => boolean}
   */
  isIrreversibleId: (id) => /(^|\.)(confirm|remove|delete)(\.|$)/.test(String(id || "")),

  /**
   * 이 레지스트리 id 가 **화면을 이동시키는** 동작인가(단계 전환·모듈 전환 등).
   *
   * LLM 이 답변 끝에 흐름 제안으로 붙인 이동은 자동 실행하지 않고 클릭을 받는다 — 안 그러면
   *   묻지도 않았는데 보고 있던 화면이 바뀐다. 사용자가 "다음 단계"라고 **말해서** 나온
   *   fast-path 이동(origin=fastpath)은 그대로 즉시 실행되므로 조작이 늘지 않는다.
   *
   * ⚠️ isIrreversibleId 와 같은 규약 — 화면이 제공하면 기본값을 **대체**한다.
   *   자기 화면의 이동성 id 를 빠짐없이 포함시켜야 한다.
   *   (백엔드 verb 기준 가드 navigate 는 챗이 별도로 항상 적용한다 — 이중 안전망.)
   * @type {(dispatchId: string) => boolean}
   */
  isNavigationId: (id) => /(^|\.)(nav|step|go|move)(\.|$)/.test(String(id || "")),

  /**
   * 슬래시 팔레트 카테고리. 매칭 대상 id 는 전부 화면 레지스트리가 만든 것이라 화면이 소유한다.
   *   { key, labelKey, descKey, iconName, match(id), weight? }
   *   labelKey/descKey — i18n **키**를 넘긴다. 어댑터는 identity 고정 객체라 안에서 t() 를 부르면
   *                      언어를 바꿔도 갱신되지 않는다. 번역은 챗이 렌더 시점에 한다.
   *   iconName        — React 컴포넌트가 아니라 **문자열**(화면→챗으로 컴포넌트가 흐르지 않게).
   *                      해석은 agentChatConfig.SLASH_ICONS.
   *   weight          — 정렬 가중치(작을수록 위). 생략 시 0.
   * @type {Array<object>}
   */
  slashCategories: [],

  /**
   * 슬래시 팔레트 개별 항목 정렬 가중치(작을수록 위). 화면 레지스트리 id 규칙을 안다.
   *   예: 단계 이동·자동 수행·엔진 실행처럼 여러 단계에 걸치는 전환성 액션은 아래로.
   * @type {(dispatchId: string) => number}
   */
  slashWeight: () => 0,
};

/** 부분 어댑터를 기본값과 병합한다(미제공 필드는 무정책 기본값). */
export function resolveAdapter(adapter) {
  if (!adapter) return DEFAULT_ADAPTER;
  return { ...DEFAULT_ADAPTER, ...adapter };
}

export default DEFAULT_ADAPTER;
